import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Plus,
  PawPrint,
  LogOut,
  Users,
  Link2,
  KanbanSquare,
  LayoutDashboard,
  BarChart3,
  Layers,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth, can } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { daysFromToday } from "@/lib/dateHelpers";

import DashboardStats from "@/components/dashboard/DashboardStats";
import ClientList from "@/components/dashboard/ClientList";
import ClientProfile from "@/components/dashboard/ClientProfile";
import PaymentModal from "@/components/dashboard/PaymentModal";
import ClientFormModal from "@/components/dashboard/ClientFormModal";
import CSVExportButtons from "@/components/dashboard/CSVExportButtons";
import StaffManagementModal from "@/components/StaffManagementModal";
import LeadPipeline from "@/components/LeadPipeline";
import AnalyticsView from "@/components/AnalyticsView";
import DemoModeBanner from "@/components/DemoModeBanner";
import SampleDataToggle from "@/components/dashboard/SampleDataToggle";
import SupportLink from "@/components/SupportLink";
import WelcomeTour from "@/components/onboarding/WelcomeTour";
import ProgramsModal from "@/components/programs/ProgramsModal";
import EmailHealthBanner from "@/components/dashboard/EmailHealthBanner";

export default function DogTrainerCustomerManager() {
  const [clients, setClients] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentRefreshKey, setPaymentRefreshKey] = useState(0);
  const [showStaffMgmt, setShowStaffMgmt] = useState(false);
  const [showPrograms, setShowPrograms] = useState(false);
  const [view, setView] = useState("dashboard"); // dashboard | pipeline
  const [loading, setLoading] = useState(true);
  const [showTour, setShowTour] = useState(false);
  const { user: currentUser, logout } = useAuth();

  useEffect(() => {
    fetchClients(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // First-run welcome tour: invited non-admin, non-demo users only.
  useEffect(() => {
    if (!currentUser) return;
    if (currentUser.is_demo) return;
    if (currentUser.role === "admin") return;
    if (currentUser.tourCompleted) return;
    setShowTour(true);
  }, [currentUser]);

  async function fetchClients(initial = false) {
    try {
      const res = await api.get(`/clients`);
      setClients(res.data);
      if (initial && res.data.length > 0) setSelectedId(res.data[0].id);
    } catch (err) {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  }

  const filteredClients = useMemo(() => {
    const q = query.toLowerCase();
    return clients.filter((c) => {
      const matchesSearch = [c.ownerName, c.dogName, c.breed, c.packageType, c.status]
        .join(" ")
        .toLowerCase()
        .includes(q);
      const matchesStatus = statusFilter === "All" || c.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [clients, query, statusFilter]);

  const selectedClient =
    clients.find((c) => c.id === selectedId) || filteredClients[0] || clients[0];

  const stats = useMemo(() => {
    const weekly = clients.filter((c) => {
      const d = daysFromToday(c.nextSession);
      return d !== null && d >= 0 && d <= 7;
    }).length;
    return {
      active: clients.filter((c) => c.status === "Active").length,
      leads: clients.filter((c) => c.status === "Lead").length,
      followUps: clients.filter((c) => c.status === "Needs Follow-up").length,
      balance: clients.reduce((sum, c) => sum + Number(c.balance || 0), 0),
      weekly,
    };
  }, [clients]);

  const upcoming = useMemo(() => {
    return clients
      .filter((c) => {
        const d = daysFromToday(c.nextSession);
        return d !== null && d >= 0;
      })
      .sort((a, b) => a.nextSession.localeCompare(b.nextSession))
      .slice(0, 8);
  }, [clients]);

  function openNewForm() {
    setEditingClient(null);
    setShowForm(true);
  }
  function openEditForm(client) {
    setEditingClient(client);
    setShowForm(true);
  }

  async function saveClient(form) {
    try {
      if (editingClient) {
        const payload = { ...form };
        delete payload.id;
        delete payload.sessions;
        delete payload.createdAt;
        const res = await api.put(`/clients/${editingClient.id}`, payload);
        setClients((prev) => prev.map((c) => (c.id === editingClient.id ? res.data : c)));
        toast.success("Client updated");
      } else {
        const res = await api.post(`/clients`, form);
        setClients((prev) => [res.data, ...prev]);
        setSelectedId(res.data.id);
        toast.success("Client added");
      }
      setShowForm(false);
      setEditingClient(null);
    } catch (err) {
      toast.error("Failed to save client");
    }
  }

  async function deleteClient(id) {
    if (!window.confirm("Delete this client and all their session notes?")) return;
    try {
      await api.delete(`/clients/${id}`);
      const remaining = clients.filter((c) => c.id !== id);
      setClients(remaining);
      setSelectedId(remaining[0]?.id || null);
      toast.success("Client deleted");
    } catch (err) {
      toast.error("Failed to delete client");
    }
  }

  async function addSession(sessionNote) {
    if (!selectedClient) return false;
    try {
      const res = await api.post(`/clients/${selectedClient.id}/sessions`, sessionNote);
      setClients((prev) => prev.map((c) => (c.id === selectedClient.id ? res.data : c)));
      toast.success("Session note saved");
      return true;
    } catch (err) {
      toast.error("Failed to save session note");
      return false;
    }
  }

  async function updateClientField(field, value) {
    if (!selectedClient) return;
    const original = selectedClient[field];
    setClients((prev) =>
      prev.map((c) => (c.id === selectedClient.id ? { ...c, [field]: value } : c))
    );
    try {
      await api.put(`/clients/${selectedClient.id}`, { [field]: value });
    } catch (err) {
      setClients((prev) =>
        prev.map((c) => (c.id === selectedClient.id ? { ...c, [field]: original } : c))
      );
      toast.error("Failed to update");
    }
  }

  async function recordPayment(amount, note) {
    if (!selectedClient) return;
    try {
      const res = await api.post(`/clients/${selectedClient.id}/payments`, { amount, note });
      setClients((prev) => prev.map((c) => (c.id === selectedClient.id ? res.data.client : c)));
      setShowPaymentForm(false);
      setPaymentRefreshKey((k) => k + 1);
      toast.success(`Payment of $${amount} recorded`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record payment");
    }
  }

  return (
    <div data-testid="dog-trainer-app" className="min-h-screen bg-slate-50 text-slate-950">
      <DemoModeBanner onReset={() => fetchClients(true)} />
      <EmailHealthBanner user={currentUser} />
      <div className="p-4 pb-24 md:p-8 md:pb-20">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between"
        >
          <Link
            to="/"
            data-testid="dashboard-home-link"
            title="Back to Pawbook home"
            className="group flex items-center gap-3 rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
          >
            <div className="rounded-2xl bg-slate-900 p-3 text-white shadow-sm transition-colors group-hover:bg-slate-800">
              <PawPrint size={26} />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight group-hover:text-slate-700">
                Dog Trainer Client Manager
              </h1>
              <p className="text-slate-600">
                Track leads, clients, dogs, sessions, homework, payments, and follow-ups.
              </p>
            </div>
          </Link>

          <div className="flex flex-wrap items-center gap-2">
            {can(currentUser, "exportData") && <CSVExportButtons />}
            {can(currentUser, "manageUsers") && !currentUser?.is_demo && (
              <SampleDataToggle onChange={() => fetchClients(false)} />
            )}
            {can(currentUser, "manageTrainingPrograms") && (
              <Button
                data-testid="manage-programs-btn"
                variant="outline"
                onClick={() => setShowPrograms(true)}
                className="rounded-2xl"
              >
                <Layers size={16} className="mr-2" />
                Programs
              </Button>
            )}
            {can(currentUser, "manageUsers") && !currentUser?.is_demo && (
              <Button
                data-testid="manage-users-btn"
                variant="outline"
                onClick={() => setShowStaffMgmt(true)}
                className="rounded-2xl"
              >
                <Users size={16} className="mr-2" />
                Staff
              </Button>
            )}
            {can(currentUser, "createClient") && (
              <Button
                data-testid="share-intake-btn"
                variant="outline"
                onClick={() => {
                  const url = `${window.location.origin}/intake`;
                  try {
                    navigator.clipboard?.writeText(url);
                  } catch (e) { /* clipboard unavailable */ }
                  toast.success(`Public intake link copied: ${url}`);
                }}
                className="rounded-2xl"
                title="Copy public intake form URL"
              >
                <Link2 size={16} className="mr-2" />
                Intake Link
              </Button>
            )}
            {can(currentUser, "createClient") && (
              <Button
                data-testid="add-client-btn"
                onClick={openNewForm}
                className="rounded-2xl px-5 py-6 text-base shadow-sm"
              >
                <Plus className="mr-2" size={18} /> Add Client
              </Button>
            )}
            <div className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2">
              <div className="text-right text-xs leading-tight">
                <p className="font-semibold text-slate-900" data-testid="current-user-email">
                  {currentUser?.name || currentUser?.email}
                </p>
                <p className="text-slate-500" data-testid="current-user-role">
                  {currentUser?.role === "front_desk" ? "Front Desk" : currentUser?.role}
                </p>
              </div>
              <Button
                data-testid="logout-btn"
                variant="outline"
                onClick={logout}
                className="rounded-xl px-2"
                title="Sign out"
              >
                <LogOut size={16} />
              </Button>
            </div>
          </div>
        </motion.div>

        <div className="-mt-2 flex items-center justify-end">
          <SupportLink />
        </div>

        {/* View toggle */}
        <div className="flex items-center gap-1 self-start rounded-2xl border border-slate-200 bg-white p-1" data-testid="view-toggle">
          <button
            data-testid="view-toggle-dashboard"
            onClick={() => setView("dashboard")}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
              view === "dashboard" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <LayoutDashboard size={16} /> Dashboard
          </button>
          <button
            data-testid="view-toggle-pipeline"
            onClick={() => setView("pipeline")}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
              view === "pipeline" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <KanbanSquare size={16} /> Pipeline
          </button>
          <button
            data-testid="view-toggle-analytics"
            onClick={() => setView("analytics")}
            className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition ${
              view === "analytics" ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            <BarChart3 size={16} /> Analytics
          </button>
        </div>

        {view === "analytics" ? (
          <AnalyticsView
            onOpenClient={(id) => {
              setSelectedId(id);
              setView("dashboard");
            }}
          />
        ) : view === "pipeline" ? (
          <LeadPipeline
            clients={clients}
            onClientsChange={setClients}
            onOpenClient={(id) => {
              setSelectedId(id);
              setView("dashboard");
            }}
          />
        ) : (
          <>
            <DashboardStats stats={stats} upcoming={upcoming} onSelectClient={setSelectedId} />

            <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
              <ClientList
                clients={filteredClients}
                selectedId={selectedClient?.id}
                query={query}
                statusFilter={statusFilter}
                loading={loading}
                onQueryChange={setQuery}
                onStatusFilterChange={setStatusFilter}
                onSelect={setSelectedId}
              />
              {selectedClient && (
                <ClientProfile
                  client={selectedClient}
                  currentUser={currentUser}
                  paymentRefreshKey={paymentRefreshKey}
                  onEdit={() => openEditForm(selectedClient)}
                  onDelete={() => deleteClient(selectedClient.id)}
                  onRecordPayment={() => setShowPaymentForm(true)}
                  onUpdateField={updateClientField}
                  onAddSession={addSession}
                />
              )}
            </div>
          </>
        )}
      </div>

      {showForm && (
        <ClientFormModal
          editingClient={editingClient}
          onClose={() => {
            setShowForm(false);
            setEditingClient(null);
          }}
          onSubmit={saveClient}
        />
      )}

      {showPaymentForm && selectedClient && (
        <PaymentModal
          client={selectedClient}
          onClose={() => setShowPaymentForm(false)}
          onSubmit={recordPayment}
        />
      )}

      {showStaffMgmt && (
        <StaffManagementModal onClose={() => setShowStaffMgmt(false)} currentUser={currentUser} />
      )}
      {showPrograms && <ProgramsModal onClose={() => setShowPrograms(false)} />}
      {showTour && <WelcomeTour onClose={() => setShowTour(false)} />}
      </div>
    </div>
  );
}
